import { create } from "zustand";
import type { RevenueStatement } from "@/types";

/**
 * Prototype-only scaffolding.
 *
 * The seeded revenue statements are grouped into named scenarios, matching the
 * spec document so the two can be read side by side. This is the single place
 * they are described: the dev bar lists them, the list filters by them, the row
 * captions name them and the testing hints are their content.
 *
 * A scenario is a property of a STATEMENT, not of a vendor — Create Music Group
 * carries two of them.
 *
 * Everything here is gated on the dev auth bypass at the call sites; with the
 * toggles off the list renders exactly like the product.
 */

/** One statement a scenario can be opened from, and what to do there. */
export interface ScenarioEntry {
  /** Period start of the statement this entry is about. */
  periodStart: string;
  /** What to do, e.g. "open Apr 2026, enter 150.00". */
  tryIt: string;
  /** Why the statements look the way they do. */
  situation: string;
  /** The amount to type, or null when the case needs no input. */
  enter: string | null;
  /** A second action after the amount, where the case needs one. */
  extraStep?: string;
  /** What to look for, one item per bullet. */
  expect: string[];
}

/** The same case walked from the list instead of from a statement row. */
export interface ScenarioVariantB {
  /** What to do, e.g. "tick Feb, Mar and Apr, then enter 150.00". */
  tryIt: string;
  situation: string;
  enter: string | null;
  expect: string[];
}

export interface Scenario {
  /** "main", "01" … */
  id: string;
  /** Full name as used in the spec document. */
  name: string;
  /** One vendor per scenario, so walking one never settles another one's data. */
  vendor: string;
  vendorName: string;
  /** Statement ids the list shows — and re-seeds — while this scenario is active. */
  statementIds: string[];
  /** The statements it can be opened from. Most have one; 03 has two. */
  entries: ScenarioEntry[];
  /** How the same case is walked in variant B. Absent for B-only scenarios. */
  variantB?: ScenarioVariantB;
  /** Scenarios that only exist in one variant. */
  onlyIn?: EntryVariant;
}

export const SCENARIOS: Scenario[] = [


  {
    id: "main",
    name: "Main flow · one payment, several statements",
    vendor: "vendor-create-music-group",
    vendorName: "Create Music Group",
    statementIds: ["cmg-2026-01", "cmg-2026-02", "cmg-2026-03", "cmg-2026-04"],
    entries: [
      {
        periodStart: "2026-04-01",
        tryIt: "open Apr 2026, enter 150.00",
        situation:
          "Feb was below the distributor's payout threshold, Mar was a correction. The distributor paid all three at once.",
        enter: "150.00",
        expect: [
          "allocation section appears",
          'Apr pinned as "this statement"',
          "Feb and Mar pre-selected",
          'header "3 selected · 152.85 USD"',
          "difference −2.85",
          "the split sums to exactly 150.00",
        ],
      },
    ],
    variantB: {
      tryIt: "tick Feb, Mar and Apr, then enter 150.00",
      situation:
        "Feb was below the distributor's payout threshold, Mar was a correction. The distributor paid all three at once.",
      enter: "150.00",
      expect: [
        "total 152.85",
        "difference −2.85",
        "the split sums to exactly 150.00",
      ],
    },
  },

  {
    id: "01",
    name: "01 · Payment exceeds the selection",
    vendor: "vendor-label-worx",
    vendorName: "Label Worx",
    statementIds: ["lwx-2026-02", "lwx-2026-03"],
    entries: [
      {
        periodStart: "2026-03-01",
        tryIt: "open Mar 2026, enter 500.00",
        situation: "The transfer is larger than everything outstanding for this vendor.",
        enter: "500.00",
        expect: [
          "Feb pre-selected",
          "total 320.00",
          'info banner "The payment is 180.00 USD more than the selected statements. Check the selection…"',
          "nothing blocked, reason optional",
        ],
      },
    ],
    variantB: {
      tryIt: "tick Feb and Mar, then enter 500.00",
      situation: "The transfer is larger than everything outstanding for this vendor.",
      enter: "500.00",
      expect: [
        "total 320.00",
        "info banner that the payment is larger than the selected statements",
        "nothing blocked, reason optional",
      ],
    },
  },
  {
    id: "02",
    name: "02 · Selection exceeds the payment",
    vendor: "vendor-northbound-digital",
    vendorName: "Northbound Digital",
    statementIds: ["nbd-2026-01", "nbd-2026-02", "nbd-2026-03"],
    entries: [
      {
        periodStart: "2026-03-01",
        tryIt: "open Mar 2026, enter 160.00, then also tick Jan",
        situation: "The user over-selects on purpose.",
        enter: "160.00",
        extraStep: "then also tick Jan",
        expect: [
          "after 160.00 — Feb pre-selected, total 165.25, difference −5.25",
          'after ticking Jan — total 931.63 and an info banner "The selected statements are 771.63 USD more than the payment…"',
          "still not blocked, reason still optional",
        ],
      },
    ],
    variantB: {
      tryIt: "tick Jan, Feb and Mar, then enter 160.00",
      situation: "The user over-selects on purpose.",
      enter: "160.00",
      expect: [
        "total 931.63",
        "info banner that the selection is much larger than the payment",
        "still not blocked, reason still optional",
      ],
    },
  },
  {
    id: "03",
    name: "03 · The statement is a correction",
    vendor: "vendor-eastgate-records",
    vendorName: "Eastgate Records",
    statementIds: ["eas-2026-02", "eas-2026-03"],
    entries: [
      {
        periodStart: "2026-02-01",
        tryIt: "open Feb 2026, no amount needed",
        situation: "A correction of −41.20. It is never settled on its own.",
        enter: null,
        expect: [
          "read-only vendor, period and statement value, then an info banner and Close",
          "no amount field, no list, no disabled button",
        ],
      },
    ],
    variantB: {
      tryIt: 'open Feb 2026 from its row menu, or tick it and look at the bar',
      situation: "A correction of −41.20. It is never settled on its own.",
      enter: null,
      expect: [
        'from the row menu — the same panel in both variants: vendor, period and statement value read-only, then an info banner and Close',
        'from the bar with only Feb ticked — "Mark as settled" is disabled, with "A correction can\'t be settled on its own. Select the statement the distributor paid as well." on hover',
        "Delete and Download stay enabled either way",
      ],
    },
  },
  {
    id: "04",
    name: "04 · The amounts match exactly",
    vendor: "vendor-symphonic",
    vendorName: "Symphonic Distribution",
    statementIds: ["sym-2026-04"],
    entries: [
      {
        periodStart: "2026-04-01",
        tryIt: "open Apr 2026, enter 4180.20",
        situation: "The vendor's only unsettled statement, paid in full.",
        enter: "4180.20",
        expect: [
          "no allocation section — nothing else to allocate to",
          "no reason field, no difference row",
        ],
      },
    ],
    variantB: {
      tryIt: "tick Apr, then enter 4180.20",
      situation: "The vendor's only unsettled statement, paid in full.",
      enter: "4180.20",
      expect: ["no reason field, no difference row"],
    },
  },
  {
    // In A this is about when the allocation section reveals itself. In B there
    // is nothing to reveal, so it is simply the main flow with a negative in the
    // set — which is the point worth showing.
    id: "06",
    name: "06 · A correction makes the payment smaller than the statement",
    vendor: "vendor-eastgate-records",
    vendorName: "Eastgate Records",
    statementIds: ["eas-2026-02", "eas-2026-03"],
    entries: [
      {
        periodStart: "2026-03-01",
        tryIt: "open Mar 2026, enter 268.80",
        situation:
          "The distributor paid 268.80 for Mar: 310.00 less the 41.20 correction from Feb. The payment is smaller than the statement it was opened from, and still covers both.",
        enter: "268.80",
        expect: [
          "allocation section appears even though the payment is smaller than Mar",
          'Mar pinned as "this statement"',
          'Feb correction (\u221241.20) pre-selected and tagged "correction"',
          'header "2 selected · 268.80 USD"',
          "no difference, no reason field",
        ],
      },
    ],
    variantB: {
      tryIt: "tick Feb (−41.20) and Mar (310.00), then enter 268.80",
      situation:
        "The selected total is lower than one of the selected statements. In B that needs no special handling — it is the main flow with a negative in the set.",
      enter: "268.80",
      expect: [
        "total 268.80",
        "no difference, no reason field, no split link",
      ],
    },
  },
];

export function scenarioById(id: string | null): Scenario | null {
  return SCENARIOS.find((s) => s.id === id) ?? null;
}

/** The scenarios that exist in this variant. */
export function scenariosFor(variant: EntryVariant): Scenario[] {
  return SCENARIOS.filter((s) => !s.onlyIn || s.onlyIn === variant);
}

/** What to do and what to expect, resolved for the active variant. */
export interface ResolvedHint {
  title: string;
  tryIt: string;
  situation: string;
  enter: string | null;
  extraStep?: string;
  expect: string[];
}

/** Variant A: the hint belongs to the statement the drawer was opened from. */
export function hintForStatement(statement: RevenueStatement | null): ResolvedHint | null {
  const scenario = scenarioForStatement(statement);
  const entry = entryForStatement(statement);
  if (!scenario || !entry) return null;
  return {
    title: scenario.name,
    tryIt: entry.tryIt,
    situation: entry.situation,
    enter: entry.enter,
    extraStep: entry.extraStep,
    expect: entry.expect,
  };
}

/** Variant B: the hint belongs to the scenario being walked, not to one row. */
export function hintForScenario(id: string | null): ResolvedHint | null {
  const scenario = scenarioById(id);
  if (!scenario?.variantB) return null;
  return {
    title: scenario.name,
    tryIt: scenario.variantB.tryIt,
    situation: scenario.variantB.situation,
    enter: scenario.variantB.enter,
    expect: scenario.variantB.expect,
  };
}

/**
 * The scenario a statement is an entry point for — keyed by vendor and period,
 * so it follows the statement rather than an id. Null for statements that are
 * only context inside a scenario.
 */
export function scenarioForStatement(statement: RevenueStatement | null): Scenario | null {
  if (!statement) return null;
  return (
    SCENARIOS.find(
      (scenario) =>
        scenario.vendor === statement.vendor &&
        scenario.entries.some((entry) => entry.periodStart === statement.period_start),
    ) ?? null
  );
}

/** What to do in the drawer for this statement, if it is an entry point. */
export function entryForStatement(statement: RevenueStatement | null): ScenarioEntry | null {
  if (!statement) return null;
  const scenario = scenarioForStatement(statement);
  return scenario?.entries.find((e) => e.periodStart === statement.period_start) ?? null;
}

const LABELS_KEY = "mss-show-scenario-labels";
const HINTS_KEY = "mss-show-testing-hints";
const VARIANT_KEY = "mss-settle-entry-variant";
const NAV_KEY = "mss-full-navigation";

/** How the user gets into a settlement. Both produce the same payment record. */
export type EntryVariant = "A" | "B";

export const ENTRY_VARIANTS: { id: EntryVariant; label: string }[] = [
  { id: "A", label: "A · From a statement" },
  { id: "B", label: "B · From the list" },
];

interface ScenarioUiState {
  /** Row captions. Off by default — the list has to be able to look like the product. */
  showLabels: boolean;
  setShowLabels: (show: boolean) => void;
  /** The hint card beside the settle drawer. On by default — it is the point of the prototype. */
  showHints: boolean;
  setShowHints: (show: boolean) => void;
  /**
   * The scenario the list is filtered to. Persists across opening, using and
   * closing the drawer — only the clear control or another scenario changes it.
   */
  activeScenarioId: string | null;
  setActiveScenario: (id: string | null) => void;
  /** Which entry point the prototype offers. A is the original behaviour. */
  entryVariant: EntryVariant;
  setEntryVariant: (variant: EntryVariant) => void;
  /**
   * Show the whole product navigation. Off by default: the prototype only has
   * Revenue, and the rest would be dead links.
   */
  fullNavigation: boolean;
  setFullNavigation: (show: boolean) => void;
}

export const useScenarioUi = create<ScenarioUiState>((set) => ({
  showLabels: localStorage.getItem(LABELS_KEY) === "true",
  setShowLabels: (show) => {
    localStorage.setItem(LABELS_KEY, String(show));
    set({ showLabels: show });
  },
  showHints: localStorage.getItem(HINTS_KEY) !== "false",
  setShowHints: (show) => {
    localStorage.setItem(HINTS_KEY, String(show));
    set({ showHints: show });
  },
  // Deliberately not persisted: a reload should not leave the list mysteriously
  // filtered, and the dev bar only shows the active scenario while it is set.
  activeScenarioId: null,
  setActiveScenario: (id) => set({ activeScenarioId: id }),
  entryVariant: localStorage.getItem(VARIANT_KEY) === "B" ? "B" : "A",
  setEntryVariant: (variant) => {
    localStorage.setItem(VARIANT_KEY, variant);
    set({ entryVariant: variant });
  },
  fullNavigation: localStorage.getItem(NAV_KEY) === "true",
  setFullNavigation: (show) => {
    localStorage.setItem(NAV_KEY, String(show));
    set({ fullNavigation: show });
  },
}));

/** The statements the list shows for the active scenario, or all of them. */
export function filterByScenario(
  statements: RevenueStatement[],
  activeScenarioId: string | null,
): RevenueStatement[] {
  const scenario = scenarioById(activeScenarioId);
  if (!scenario) return statements;
  return statements.filter((s) => scenario.statementIds.includes(s.id));
}
